import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import CloseIcon from "@mui/icons-material/Close"
import { Dialog, DialogContent, Grid, IconButton, useMediaQuery, useTheme as useMuiTheme } from "@mui/material"
import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import { ModelType } from "../../../components/status/ModelType"
import { KonanCopyToClipboard } from "../../../components/utility/KonanCopyToClipboard"
import { getTheme } from "../../../hooks/UseTheme"
import type { MetaDataDialogProps } from "../interfaces"

/**
 * Dialog component that displays model metadata
 *
 * @param {boolean} isOpen - Controls the visibility state of the dialog
 * @param {string} Id - Unique identifier for the model metadata
 * @param {Dispatch} setClose - Function to handle dialog close state
 * @param {Model} model - Model object containing metadata information to display
 * @return {React.ReactElement}
 */
export function MetaDataDialog(props: Readonly<MetaDataDialogProps>): ReactElement {
  const { isOpen, Id, setClose, model } = props

  const muiTheme = useMuiTheme()
  const theme = getTheme()
  const { t } = useTranslation("models")

  return (
    <Dialog
      open={isOpen}
      onClose={() => setClose(false)}
      maxWidth="xs"
      fullScreen={useMediaQuery(muiTheme.breakpoints.down("md"))}
    >
      <DialogContent className="dialog-content-base">
        <Grid item container justifyContent="space-between" xs={12}>
          <Grid item>
            <ModelType modelState={model?.state} size="small" />
          </Grid>

          <Grid item>
            <IconButton id="close" onClick={() => setClose(false)} size="small" className={"close-icon-button"}>
              <CloseIcon style={{ color: theme.palette.gray.background[1] }} />
            </IconButton>
          </Grid>
        </Grid>

        <Typography variant="h2-bold">{model?.name}</Typography>
        <Grid mt={2}>
          <Typography variant="h3-bold">{t("dialogs.metadata.title")}</Typography>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Typography variant="label-bold">UUID</Typography>
          <Typography variant="p" className="clipboard-box-model-info" noWrap>
            <Tooltip title={Id} placement="top">
              <KonanCopyToClipboard text={Id} />
              {Id}
            </Tooltip>
          </Typography>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Typography
            variant="label-bold"
            tooltip={t("dialogs.metadata.image_tooltip")}
            tooltipVerticalAlign="middle"
            tooltipIconSize={16}
          >
            {t("dialogs.metadata.image")}
          </Typography>

          <Typography variant="p" noWrap className="clipboard-box-model-info">
            <Tooltip title={model?.image_url} width="100%" placement="top">
              <Typography variant="p" noWrap style={{ textOverflow: "ellipsis", width: "100%" }}>
                <KonanCopyToClipboard text={model?.image_url} />
                {model?.image_url}
              </Typography>
            </Tooltip>
          </Typography>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Typography
            variant="label-bold"
            tooltip={t("dialogs.metadata.port_tooltip")}
            tooltipIconSize={16}
            tooltipVerticalAlign="middle"
          >
            {t("dialogs.metadata.port")}
          </Typography>
          <Typography variant="p" className={"clipboard-box-model-info"} noWrap>
            <KonanCopyToClipboard text={`${model?.exposed_port}`} />
            {model?.exposed_port}
          </Typography>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
